/**
 * M7 S8: the bundle (spec §2.5, §8.1; break-it EX-2). Before a hand-in the player picks up to `case.json bundleMax`
 * logs and items; the hearing, the Wire or the buyer quotes them back, and `case.ts` scores them.
 *
 * Entries (`BundleEntry`):
 * - `{ kind: 'item', id }`: a story item the player has (`held`, `copy`, `withheld`). It carries **every** log of that
 *   item (the report core is both Okafor's report and the routing slip), weighed as an original or as a copy.
 * - `{ kind: 'log', id }`: an authored log in the ledger. A log that is a story item's contents (`item` set) is the
 *   same entry as that item, so it never counts twice; the repeatable templates themselves are not pickable.
 * - `{ kind: 'log', id: '<template>#<n>' }`: the n-th repeatable instance of a link (a corroboration, or for the
 *   `knowledge` link a meter proof), 1-based in the order taken.
 *
 * **One entry per item id:** entries are keyed (`bundleEntryKey`); adding an entry whose key is already in the
 * bundle replaces it, and an item entry (the original) always wins over its log, never adds to it. The stipend is
 * never an entry (spec §2.5, DS-2).
 *
 * Pure: Career-in, Career-out.
 */
import type { Career } from '../career/types'
import { itemWithPlayer } from './items'
import { hasLog } from './logs'
import { meterProofsOf } from './meter'
import type { CaseConfig, EvidenceLog } from './schemas'
import { storyOf, withStory } from './state'
import { CASE_LINKS, STORY_ITEM_IDS, type BundleEntry, type CaseLink, type FinalePath, type StoryItemId } from './types'

/** What the bundle and the case read: `storyData()` satisfies it (tests pass their own). */
export interface CaseData {
  logsById: ReadonlyMap<string, EvidenceLog>
  case: CaseConfig
}

const ITEM_IDS: ReadonlySet<string> = new Set(STORY_ITEM_IDS)

function isItemId(id: string): id is StoryItemId {
  return ITEM_IDS.has(id)
}

/** A repeatable instance id: `<template>#<n>`. */
export function instanceId(template: string, n: number): string {
  return `${template}#${n}`
}

/** The link and index of a repeatable instance id, or null when `id` isn't one. */
export function parseInstance(id: string, cfg: CaseConfig): { link: CaseLink; n: number } | null {
  const hash = id.lastIndexOf('#')
  if (hash <= 0) return null
  const template = id.slice(0, hash)
  const n = Number(id.slice(hash + 1))
  if (!Number.isInteger(n) || n < 1) return null
  for (const link of CASE_LINKS) {
    if (cfg.corroboration.templates[link] === template) return { link, n }
  }
  return null
}

/** How many repeatable instances the career holds for `link` (meter proofs for `knowledge`). */
export function instanceCount(career: Career, link: CaseLink): number {
  if (link === 'knowledge') return meterProofsOf(career).length
  let n = 0
  for (const c of storyOf(career).corroborations ?? []) if (c.link === link) n++
  return n
}

/**
 * The entry's identity in a bundle: `item:<id>` for an item or a log of an item's contents, `log:<id>` otherwise.
 * Two entries with one key are one entry (spec §2.5: one entry per item id).
 */
export function bundleEntryKey(entry: BundleEntry, logs: CaseData['logsById']): string {
  if (entry.kind === 'item') return `item:${entry.id}`
  const item = logs.get(entry.id)?.item
  return item !== undefined ? `item:${item}` : `log:${entry.id}`
}

/** The player can put `entry` in a bundle now. */
export function entryAvailable(career: Career, entry: BundleEntry, data: CaseData): boolean {
  if (entry.kind === 'item') {
    if (!isItemId(entry.id)) return false
    const item = storyOf(career).items.find((i) => i.id === entry.id)
    return item !== undefined && itemWithPlayer(item)
  }
  const log = data.logsById.get(entry.id)
  if (log !== undefined) {
    if (log.repeatable) return false
    if (log.item !== undefined) return entryAvailable(career, { kind: 'item', id: log.item }, data)
    return hasLog(career, log.id)
  }
  const inst = parseInstance(entry.id, data.case)
  return inst !== null && inst.n <= instanceCount(career, inst.link)
}

/** Every entry the player could pick (items first, then logs in the ledger, then repeatable instances). */
export function availableEntries(career: Career, data: CaseData): BundleEntry[] {
  const out: BundleEntry[] = []
  for (const item of storyOf(career).items) if (itemWithPlayer(item)) out.push({ kind: 'item', id: item.id })
  for (const log of data.logsById.values()) {
    if (log.repeatable || log.item !== undefined) continue
    if (hasLog(career, log.id)) out.push({ kind: 'log', id: log.id })
  }
  for (const link of CASE_LINKS) {
    const n = instanceCount(career, link)
    for (let i = 1; i <= n; i++) out.push({ kind: 'log', id: instanceId(data.case.corroboration.templates[link], i) })
  }
  return out
}

/** Collapses entries to one per key (an item entry replaces its log) and keeps the first `bundleMax`. */
export function normalizeBundle(entries: readonly BundleEntry[], data: CaseData): BundleEntry[] {
  let out: BundleEntry[] = []
  for (const e of entries) out = addOrReplace(out, e, data.logsById)
  return out.slice(0, data.case.bundleMax)
}

function addOrReplace(entries: readonly BundleEntry[], entry: BundleEntry, logs: CaseData['logsById']): BundleEntry[] {
  const key = bundleEntryKey(entry, logs)
  const at = entries.findIndex((e) => bundleEntryKey(e, logs) === key)
  if (at < 0) return [...entries, entry]
  const existing = entries[at]
  // An original (the item entry) replaces its copy (the item's log); never the other way round.
  if (existing === undefined || (existing.kind === 'item' && entry.kind === 'log')) return [...entries]
  return entries.map((e, i) => (i === at ? entry : e))
}

export type BundleRefusal = 'full' | 'unavailable'
export type BundleResult = { ok: true; entries: BundleEntry[] } | { ok: false; reason: BundleRefusal }

/**
 * Adds `entry` to a bundle being picked. An entry with the same key replaces the one there (one entry per item
 * id); a new key is refused when the bundle is full or the player doesn't have it.
 */
export function addToBundle(career: Career, entries: readonly BundleEntry[], entry: BundleEntry, data: CaseData): BundleResult {
  if (!entryAvailable(career, entry, data)) return { ok: false, reason: 'unavailable' }
  const key = bundleEntryKey(entry, data.logsById)
  const present = entries.some((e) => bundleEntryKey(e, data.logsById) === key)
  if (!present && entries.length >= data.case.bundleMax) return { ok: false, reason: 'full' }
  return { ok: true, entries: addOrReplace(entries, entry, data.logsById) }
}

/** Removes the entry with `entry`'s key. */
export function removeFromBundle(entries: readonly BundleEntry[], entry: BundleEntry, data: CaseData): BundleEntry[] {
  const key = bundleEntryKey(entry, data.logsById)
  return entries.filter((e) => bundleEntryKey(e, data.logsById) !== key)
}

/**
 * Records the bundle handed in for `path` (`career.story.bundles`, one per path; a later pick for the same path
 * replaces it). Refused when any entry isn't available; the entries are normalized first.
 */
export function pickBundle(
  career: Career,
  path: FinalePath,
  entries: readonly BundleEntry[],
  data: CaseData,
): { ok: true; career: Career } | { ok: false; reason: BundleRefusal } {
  if (!entries.every((e) => entryAvailable(career, e, data))) return { ok: false, reason: 'unavailable' }
  if (new Set(entries.map((e) => bundleEntryKey(e, data.logsById))).size > data.case.bundleMax) return { ok: false, reason: 'full' }
  const picked = normalizeBundle(entries, data)
  const next = withStory(career, (s) => ({
    ...s,
    bundles: [...s.bundles.filter((b) => b.path !== path), { path, entries: picked }],
  }))
  return { ok: true, career: next }
}
